import { CatalogViewFilter } from "@ext/catalog/views/components/Sections/EditCatalogView/CatalogViewFilter/CatalogViewFilter";
import { CatalogViewGrouping } from "@ext/catalog/views/components/Sections/EditCatalogView/CatalogViewGrouping/CatalogViewGrouping";
import { CatalogViewProperty } from "@ext/catalog/views/components/Sections/EditCatalogView/CatalogViewProperty/CatalogViewProperty";
import type { EditCatalogViewSchema } from "@ext/catalog/views/logic/schema/getEditCatalogViewSchema";
import { resolveGroupBy } from "@ext/catalog/views/logic/utils/resolveGroupBy";
import type { PropertyID, PropertyValue } from "@ext/properties/models";
import { memo, useCallback } from "react";
import { type UseFormReturn, useWatch } from "react-hook-form";

export interface CatalogViewDraft {
	filters: PropertyValue[];
	properties: PropertyValue[];
	groupBy: PropertyID[];
}

export interface EditCatalogViewProps {
	form: UseFormReturn<EditCatalogViewSchema>;
	disabled?: boolean;
	onChange: (draft: CatalogViewDraft) => void;
}

export const EditCatalogView = memo(({ form, disabled, onChange }: EditCatalogViewProps) => {
	// useWatch, not form.watch: this component is memoized and only its own subscription re-renders it.
	const groupBy = resolveGroupBy(useWatch({ control: form.control, name: "groupBy" }));

	const draft = useCallback(
		(): CatalogViewDraft => ({
			filters: form.getValues("filters") as PropertyValue[],
			properties: form.getValues("properties") as PropertyValue[],
			groupBy: resolveGroupBy(form.getValues("groupBy")),
		}),
		[form],
	);

	const onGroupingChange = useCallback(
		(next: PropertyID[]) => {
			form.setValue("groupBy", next);
			onChange({ ...draft(), groupBy: next });
		},
		[form, onChange, draft],
	);

	const onFiltersChange = useCallback(
		(filters: PropertyValue[]) => onChange({ ...draft(), filters }),
		[onChange, draft],
	);

	const onPropertiesChange = useCallback(
		(properties: PropertyValue[]) => onChange({ ...draft(), properties }),
		[onChange, draft],
	);

	return (
		<>
			<CatalogViewFilter disabled={disabled} form={form} onChange={onFiltersChange} />
			<CatalogViewGrouping disabled={disabled} groupBy={groupBy} onChange={onGroupingChange} />
			<CatalogViewProperty disabled={disabled} form={form} onChange={onPropertiesChange} />
		</>
	);
});
